import { createDebugger } from './debug.js';
import { validateNorwegianOrgNumber } from './norwegian-validation.js';

const debug = createDebugger('vat-toggle', false);

const STORAGE_KEY = 'esk_show_vat';

function applyVatState(showVat) {
    document.documentElement.classList.toggle('show-vat', showVat);
    const checkbox = document.getElementById('vat-toggle');
    if (checkbox) checkbox.checked = showVat;
    debug.log('VAT display:', showVat ? 'including VAT' : 'excluding VAT');
}

function setVatPreference(showVat) {
    localStorage.setItem(STORAGE_KEY, showVat ? 'true' : 'false');
    applyVatState(showVat);
}

function shouldShowPrompt() {
    return localStorage.getItem(STORAGE_KEY) === null;
}

function copyExemptionContent() {
    const source = document.getElementById('ooTaxExemption');
    const target = document.getElementById('ooTaxExemptionModal');
    if (source && target && source.innerHTML.trim()) {
        target.innerHTML = source.innerHTML;
    }
}

function lockModal(modal) {
    modal.setAttribute('data-locked', '');
    const origClose = modal.close.bind(modal);
    modal._origClose = origClose;
    modal.close = () => {
        debug.log('Close suppressed — modal is locked');
    };
}

function unlockModal(modal) {
    modal.removeAttribute('data-locked');
    if (modal._origClose) {
        modal.close = modal._origClose;
        modal._origClose = null;
    }
}

function showOrgError(message) {
    const el = document.getElementById('vat-org-error');
    if (!el) return;
    el.textContent = message;
    el.classList.remove('hidden');
}

function hideOrgError() {
    const el = document.getElementById('vat-org-error');
    if (el) el.classList.add('hidden');
}

function setupOrgNumberForm(modal) {
    const form = document.getElementById('vat-org-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        hideOrgError();

        const input = document.getElementById('vat-org-input');
        const raw = input?.value.trim() ?? '';
        const cleaned = raw.replace(/[\s.-]/g, '');

        if (!cleaned) {
            showOrgError('Please enter an organization number');
            return;
        }
        if (!/^\d{9}$/.test(cleaned)) {
            showOrgError('Organization number must be 9 digits');
            return;
        }
        if (!validateNorwegianOrgNumber(cleaned)) {
            showOrgError('Invalid organization number');
            return;
        }

        const submitBtn = document.getElementById('vat-org-submit');
        const submitText = document.getElementById('vat-org-submit-text');
        const submitLoading = document.getElementById('vat-org-submit-loading');

        if (submitBtn) submitBtn.disabled = true;
        if (submitText) submitText.classList.add('hidden');
        if (submitLoading) submitLoading.classList.remove('hidden');

        try {
            const formData = new FormData();
            formData.append('attributes[org_number]', cleaned);
            formData.append('attributes[org_number_skipped]', '');

            const res = await fetch('/cart/update.js', { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Cart update failed');

            debug.log('Org number saved:', cleaned);
            unlockModal(modal);
            modal.close();
        } catch (err) {
            debug.error('Failed to save org number:', err);
            showOrgError('Failed to save. Please try again.');
            if (submitBtn) submitBtn.disabled = false;
            if (submitText) submitText.classList.remove('hidden');
            if (submitLoading) submitLoading.classList.add('hidden');
        }
    });
}

function setupVatPromptModal() {
    const modal = document.getElementById('vat-preference-modal');
    if (!modal) return;

    if (shouldShowPrompt()) {
        setTimeout(() => modal.open(), 600);
    }

    const country = (modal.dataset.country || '').toUpperCase();
    const hasOrgNumber = modal.dataset.hasOrgNumber === 'true';

    // Track choice made inside the modal — only persisted on close
    let pendingChoice = null;

    // "Private person" → preview with VAT, persist on close
    document.getElementById('vat-choose-private')?.addEventListener('click', () => {
        pendingChoice = true;
        applyVatState(true);
    });

    // "Business" → preview without VAT, persist on close
    document.getElementById('vat-choose-business')?.addEventListener('click', () => {
        pendingChoice = false;
        applyVatState(false);
        if (country === 'NO' && !hasOrgNumber) {
            lockModal(modal);
            modal.showPage('org-number');
        } else {
            requestAnimationFrame(copyExemptionContent);
            modal.showPage('exemption');
        }
    });

    // Back button on org-number page → unlock, reset pending choice, navigate to choice page
    document.getElementById('vat-org-back')?.addEventListener('click', () => {
        unlockModal(modal);
        pendingChoice = null;
        applyVatState(localStorage.getItem(STORAGE_KEY) === 'true');
    });

    setupOrgNumberForm(modal);

    // Open VAT modal directly on the org-number page (from cart notice)
    document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-open-vat-org-page]')) return;
        lockModal(modal);
        modal.showPage('org-number');
        modal.open();
    });

    // On close: persist choice if one was made; default to private only if no preference stored yet
    modal.addEventListener('overlay:close', () => {
        if (pendingChoice !== null) {
            localStorage.setItem(STORAGE_KEY, pendingChoice ? 'true' : 'false');
            applyVatState(pendingChoice);
            debug.log('VAT preference saved on modal close:', pendingChoice);
            pendingChoice = null;
        } else if (localStorage.getItem(STORAGE_KEY) === null) {
            localStorage.setItem(STORAGE_KEY, 'true');
            applyVatState(true);
            debug.log('No choice made — defaulting to private (with VAT)');
        }
    });
}

function init() {
    const stored = localStorage.getItem(STORAGE_KEY);
    applyVatState(stored === 'true');

    // Header toggle checkbox
    document.addEventListener('change', (e) => {
        if (e.target.id === 'vat-toggle') {
            setVatPreference(e.target.checked);
        }
    });

    setupVatPromptModal();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
